import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contacts, isApiError } from '@repletoryreach/sdk-api';
import type { TokenSuggestion } from '@repletoryreach/sdk-ui/inputs';
import { useT, type Translate } from '../i18n/I18nProvider';

/**
 * The attributes a person may drop into a message with `{{`.
 *
 * One list for the three places that take them (the broadcast wizard, compose
 * and the flow builder), so the same field is called the same thing in each,
 * and so a custom field defined under Settings shows up everywhere at once.
 *
 * The grammar the services read, per recipient, at send time:
 *
 *   {{contact.name}}   {{contact.email}}   {{contact.msisdn}}   {{contact.locale}}
 *   {{contact.fields.<key>}}          one of the organisation's own fields
 *   {{contact.name|there}}            what goes out when the contact has nothing there
 *
 * Flows read the same names with `??` for the fallback (`{{contact.name ?? there}}`),
 * plus the run's own variables (`{{order_number}}`). Meta's numbered slots
 * (`{{1}}`) are the template's own placeholders and are never offered.
 */

/** A custom field as `GET /v1/contacts/fields` describes it; the SDK's own type. */
export type ContactFieldDefinition = contacts.ContactFieldDefinition;

/** The fields every contact has, in the order they are offered. */
export const CONTACT_BASE_FIELDS = ['name', 'email', 'msisdn', 'locale'] as const;
export type ContactBaseField = (typeof CONTACT_BASE_FIELDS)[number];

export const CONTACT_FIELDS_QUERY_KEY = ['contacts', 'fields'] as const;

/** What a base field is called in the list and the picker. */
export function contactFieldLabel(t: Translate, field: ContactBaseField): string {
  return t(`attributes.contact.${field}`);
}

async function fetchFieldDefinitions(): Promise<ContactFieldDefinition[]> {
  // A 404 is a stack whose contacts service predates the endpoint, which is
  // the same answer as "no custom fields": the base fields still work.
  try {
    const page = await contacts.listFields();
    return (page?.data ?? []).filter((f) => f && typeof f.key === 'string' && f.key.trim().length > 0);
  } catch (e) {
    if (isApiError(e) && e.status === 404) return [];
    throw e;
  }
}

/** The organisation's custom field definitions, cached; an error reads as none. */
export function useContactFieldDefinitions(): { fields: ContactFieldDefinition[]; loading: boolean; error: boolean } {
  const q = useQuery({
    queryKey: CONTACT_FIELDS_QUERY_KEY,
    queryFn: fetchFieldDefinitions,
    staleTime: 60_000,
    retry: false
  });
  const fields = useMemo(
    () => [...(q.data ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.label.localeCompare(b.label)),
    [q.data]
  );
  return { fields, loading: q.isPending, error: q.isError };
}

/** Only the fields still in use may be offered; a retired one keeps resolving for old drafts. */
export function activeFields(fields: ContactFieldDefinition[]): ContactFieldDefinition[] {
  return fields.filter((f) => f.is_active !== false);
}

export interface AttributeSuggestionOptions {
  /** The run's variables, when inside a flow. Listed under "Flow variables". */
  flowVariables?: string[];
  /** Offer the contact's own fields. Default: true. */
  includeContact?: boolean;
}

/**
 * The suggestions for a `TokenInput`: the contact's base fields, the
 * organisation's active custom fields and, in a flow, the run's variables.
 * A flow variable that names a contact field (`contact.name`) is listed once,
 * under Contact, since it is the same value.
 */
export function useAttributeSuggestions(opts: AttributeSuggestionOptions = {}): TokenSuggestion[] {
  const t = useT();
  const { flowVariables, includeContact = true } = opts;
  const { fields } = useContactFieldDefinitions();
  const flowKey = (flowVariables ?? []).join(' ');

  return useMemo(() => {
    const out: TokenSuggestion[] = [];
    const seen = new Set<string>();
    const push = (s: TokenSuggestion) => { if (!seen.has(s.token)) { seen.add(s.token); out.push(s); } };

    // A flow's own variables first: inside a flow they are what the author
    // is most likely reaching for.
    if (flowVariables) {
      for (const v of flowVariables) {
        if (!v || (includeContact && v.startsWith('contact.'))) continue;
        push({ token: v, label: v, group: t('attributes.group.flow') });
      }
    }

    if (includeContact) {
      const contact = t('attributes.group.contact');
      for (const f of CONTACT_BASE_FIELDS) push({ token: `contact.${f}`, label: contactFieldLabel(t, f), group: contact });
      // Anything else the flow knows about the contact (`contact.tags`) sits beside them.
      for (const v of flowVariables ?? []) {
        if (v.startsWith('contact.') && !v.startsWith('contact.fields.')) push({ token: v, label: v, group: contact });
      }
      const own = t('attributes.group.fields');
      for (const f of activeFields(fields)) {
        push({ token: `contact.fields.${f.key}`, label: f.label || f.key, group: own, description: f.description ?? undefined });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, fields, flowKey, includeContact]);
}
